import { useState, useEffect, useCallback, useMemo, useRef, useContext, createContext } from 'react';
import { useParams } from 'react-router';
import { useQuery } from 'react-query';
import { queryOptions } from '~/shared/constants';
import { buildSelectionFromPartial, selectionToFilterParams } from '~/shared/utils/cascadingNarrativeFilters';
import type { FilterHierarchyConfig } from '~/shared/utils/filterHierarchy';
import { useSearchParamsValue } from '../hooks';
import { fetchHeatmap } from './api';
import { colorRanges } from '../utils/colors';

const defaultColorScheme = colorRanges.whiteRed;

export type WidgetOverrideEntry = { values: Record<string, any>; ids: Record<string, any> };
export type WidgetOverridesMap = Record<string, WidgetOverrideEntry>;

export const WidgetOverridesContext = createContext<WidgetOverridesMap>({});

export function useNavAndFilters(navHirarchy: any, gridIndex?: number) {
  const widgetOverrides = useContext(WidgetOverridesContext);
  const { category, subcategory, subsubcategory, prism } = useParams<{
    category: string;
    subcategory: string;
    subsubcategory: string;
    prism: string;
  }>();
  const paramsFilterValues = useSearchParamsValue();

  const { navHirarchyItem, filterValues, filterValuesID } = useMemo(() => {
    let currrentPrism: any;
    if (!category && !subcategory && !subsubcategory && prism) {
      currrentPrism = navHirarchy?.find(({ id }: any) => id === prism);
    }

    const currentCategory = navHirarchy?.find(({ name }: any) => name === category);
    const currentSubcategory = subcategory && currentCategory?.items?.find(({ name }: any) => name === subcategory);
    const currentSubSubcategory =
      subsubcategory && currentSubcategory?.items?.find(({ name }: any) => name === subsubcategory);
    const baseCategory = currentSubSubcategory || currentSubcategory || currentCategory || prism;
    const baseList =
      baseCategory?.dashboards && baseCategory?.dashboards?.length > 0
        ? 'dashboards'
        : baseCategory?.items && baseCategory?.items?.length > 0
          ? 'items'
          : 'prisms';

    const getCurrentItem = (baseCategory: any, baseList: string) => {
      if (baseCategory && baseCategory[baseList] && !gridIndex && gridIndex !== 0) {
        return baseCategory[baseList].find(({ id }: any) => {
          return id === prism;
        });
      } else {
        const tempItem = baseCategory[baseList]?.find((item: any) => {
          return item.id === prism;
        });
        const returnItem: any = tempItem?.gridElements[gridIndex || 0].item;
        if (returnItem && returnItem?.displayName && tempItem?.id === prism) returnItem.displayName = tempItem.name;
        return returnItem || currrentPrism;
      }
    };

    const currItem = getCurrentItem(baseCategory, baseList);
    const resolveOptionId = (filter: any, rawValue: any) => {
      if (!filter) {
        return rawValue;
      }

      if (rawValue === undefined || rawValue === null) {
        return rawValue;
      }

      const candidate = String(rawValue);
      const options = Array.isArray(filter?.options) ? filter.options : [];

      const exactMatch = options.find((option: any) => {
        const optionValues = [option?.id, option?.name, option?.value].filter(
          value => value !== undefined && value !== null,
        );
        return optionValues.map((value: any) => String(value)).includes(candidate);
      });

      if (exactMatch) {
        return exactMatch.id ?? exactMatch.name ?? exactMatch.value ?? candidate;
      }

      return candidate;
    };

    const resolveFilterDefault = (filter: any) => {
      if (!filter) {
        return undefined;
      }

      const explicitCandidates = [
        filter?.defaultValue,
        filter?.['default-value'],
        filter?.default,
        filter?.['defaultOption'],
        filter?.['default-option'],
      ];

      const explicit = explicitCandidates.find(value => value !== undefined && value !== null && value !== '');

      if (explicit !== undefined) {
        return explicit;
      }

      const options = Array.isArray(filter?.options) ? filter.options : [];
      const flaggedOption = options.find(
        (option: any) =>
          option?.default === true ||
          option?.default === 'true' ||
          option?.isDefault === true ||
          option?.isDefault === 'true' ||
          option?.['default-option'] === true ||
          option?.['default-option'] === 'true',
      );

      if (flaggedOption) {
        return flaggedOption.id ?? flaggedOption.name ?? flaggedOption.value;
      }

      const firstOption = options[0];
      return firstOption?.id ?? firstOption?.name ?? firstOption?.value;
    };

    const defaults =
      currItem?.filters &&
      currItem?.filters?.reduce((all: any, filter: any) => {
        if (!filter?.name) {
          return all;
        }

        const rawDefault = resolveFilterDefault(filter);
        return {
          ...all,
          [filter.name]: rawDefault,
        };
      }, {} as any);

    const defaultsID =
      currItem?.filters &&
      currItem?.filters?.reduce((all: any, filter: any) => {
        const key = filter?.id ?? filter?.name;
        if (!key) {
          return all;
        }

        const rawDefault = resolveFilterDefault(filter);
        const resolved = resolveOptionId(filter, rawDefault);
        return {
          ...all,
          [key]: resolved,
        };
      }, {} as any);

    const paramsFilterValuesID: { [key: string]: any } = {};
    Object.entries(paramsFilterValues).forEach(([key, value]: any) => {
      if (key === 'Date') {
        paramsFilterValuesID[key] = value;
      } else {
        const filter = currItem?.filters?.find((candidate: any) => {
          const names = [candidate?.name, candidate?.id]
            .filter(Boolean)
            .map((token: any) => String(token).toLowerCase());
          return names.includes(String(key).toLowerCase());
        });

        paramsFilterValuesID[key] = resolveOptionId(filter, value);
      }
    });

    const narrativeFilterTree = currItem?.narrativeFilterTree as FilterHierarchyConfig | undefined;
    let narrativeDefaults = {};
    let narrativeDefaultsID = {};
    if (narrativeFilterTree?.filterHierarchy?.length) {
      const urlPartial: Record<string, string | undefined> = {};
      narrativeFilterTree.filterHierarchy.forEach(level => {
        const fromParams = paramsFilterValues[level.id] ?? paramsFilterValues[level.name];
        if (fromParams !== undefined && fromParams !== null && fromParams !== '') {
          urlPartial[level.id] = String(fromParams);
        }
      });
      const selection = buildSelectionFromPartial(narrativeFilterTree, urlPartial);
      const params = selectionToFilterParams(narrativeFilterTree, selection);
      narrativeDefaults = params.labels;
      narrativeDefaultsID = params.ids;
    }

    const overrideEntry = currItem?.id ? widgetOverrides[currItem.id] : undefined;
    const overrideValues = overrideEntry?.values ?? {};
    const overrideIds = overrideEntry?.ids ?? overrideEntry?.values ?? {};
    return {
      navHirarchyItem: currItem,
      filterValues: { ...defaults, ...narrativeDefaults, ...paramsFilterValues, ...overrideValues },
      // Resolved hierarchy ids must win over raw URL tokens (invalid click ids caused widget 500s).
      filterValuesID: { ...defaultsID, ...paramsFilterValuesID, ...narrativeDefaultsID, ...overrideIds },
    };
  }, [navHirarchy, category, subcategory, subsubcategory, prism, paramsFilterValues, gridIndex, widgetOverrides]);

  return {
    category,
    subcategory,
    subsubcategory,
    prism: navHirarchyItem?.name,
    navHirarchyItem,
    filterValues,
    filterValuesID,
    prismId: navHirarchyItem?.id,
  };
}

export function useNavAndFiltersBak(navHirarchy: any) {
  const { category, subcategory, subsubcategory, prism } = useParams<{
    category: string;
    subcategory: string;
    subsubcategory: string;
    prism: string;
  }>();
  const paramsFilterValues = useSearchParamsValue();

  const { navHirarchyItem, filterValues } = useMemo(() => {
    let currrentPrism;
    if (!category && !subcategory && !subsubcategory && prism) {
      currrentPrism = navHirarchy.find(({ id }: any) => id === prism);
    }

    const currentCategory = navHirarchy.find(({ name }: any) => name === category);
    const currentSubcategory = subcategory && currentCategory?.items?.find(({ name }: any) => name === subcategory);
    const currentSubSubcategory =
      subsubcategory && currentSubcategory?.items?.find(({ name }: any) => name === subsubcategory);
    const baseCategory = currentSubSubcategory || currentSubcategory || currentCategory || prism;
    const baseList = baseCategory?.items && baseCategory?.items?.length > 0 ? 'items' : 'prisms';

    const currItem =
      (baseCategory &&
        baseCategory[baseList] &&
        baseCategory[baseList].find(({ id }: any) => {
          return id === prism;
        })) ||
      currrentPrism;

    const defaults =
      currItem?.filters &&
      currItem?.filters?.reduce(
        (all: any, { name, options }: any) => ({
          ...all,
          [name]: options[0].id,
        }),
        {} as any,
      );

    return {
      navHirarchyItem: currItem,
      filterValues: { ...defaults, ...paramsFilterValues },
    };
  }, [navHirarchy, category, subcategory, subsubcategory, prism, paramsFilterValues]);

  return {
    category,
    subcategory,
    subsubcategory,
    prism: navHirarchyItem?.name,
    navHirarchyItem,
    filterValues,
    prismId: navHirarchyItem?.id,
  };
}

export function useHeatmapData(
  category: string,
  subcategory: string,
  subsubcategory: string,
  prism: string,
  filterValues: any,
  prismId?: string,
) {
  const [usedData, setUsedData] = useState<any>();
  const [colorScheme, setColorScheme] = useState<string[]>([]);
  const [chartTitle, setChartTitle] = useState<string>('');
  const [chartSubtitle, setChartSubtitle] = useState<string>('');
  const [chartYtitle, setChartYtitle] = useState<string>('');
  const [chartYtitle2, setChartYtitle2] = useState<string>('');
  const [chartYmax, setChartYmax] = useState<number>();
  const [chartYmin, setChartYmin] = useState<number>();
  const [smaEnabled, setSmaEnabled] = useState<boolean>(false);
  const [smaConfig, setSmaConfig] = useState<{ defaultValue: number; values: number[] }>();
  const [meanEnabled, setMeanEnabled] = useState<boolean>(false);
  const [dropdownLegendEnabled, setDropdownLegendEnabled] = useState<boolean>(false);
  const [defaultTimeWindow, setDefaultTimeWindow] = useState<string | undefined>();
  const [yAxisRangeMode, setYAxisRangeMode] = useState<'zero-based' | 'padded-visible'>('padded-visible');
  const [yAxisPaddingRatio, setYAxisPaddingRatio] = useState<number>(0.2);

  const {
    data = undefined,
    error,
    isLoading,
  } = useQuery(
    ['fetchHeatmap', category, prism, filterValues],
    () => fetchHeatmap(category, subcategory, subsubcategory, prism, filterValues, prismId),
    queryOptions,
  );

  const rawTitle = useMemo(() => {
    const candidate = (data as any)?.title;
    return typeof candidate === 'string' ? candidate : '';
  }, [data]);

  useEffect(() => {
    if (data) {
      const {
        color_scheme,
        title,
        subtitle,
        ytitle,
        ytitle2,
        highcharts_config,
        sma,
        mean,
        dropdownLegend,
        default_time_window,
      } = data as unknown as any;
      setUsedData(data);
      setColorScheme(color_scheme || defaultColorScheme || []);
      const resolvedTitle = title || '';
      setChartTitle(resolvedTitle);
      setChartSubtitle(subtitle || '');
      setChartYtitle(ytitle || '');
      setChartYtitle2(ytitle2 || '');
      setChartYmax(Number(highcharts_config?.yAxis?.max));
      setChartYmin(Number(highcharts_config?.yAxis?.min));
      setMeanEnabled(!!mean);
      setDropdownLegendEnabled(!!dropdownLegend);
      setDefaultTimeWindow(
        typeof default_time_window === 'string' && default_time_window.trim().length > 0
          ? default_time_window
          : undefined,
      );
      const rawRangeModeCandidates = [
        highcharts_config?.yAxis?.range_mode,
        highcharts_config?.yAxis_range_mode,
        highcharts_config?.range_mode,
      ];
      const rawRangeMode = rawRangeModeCandidates.find(
        (candidate): candidate is string => typeof candidate === 'string' && candidate.trim().length > 0,
      );
      const normalizedRangeMode = rawRangeMode?.trim().toLowerCase();
      setYAxisRangeMode(normalizedRangeMode === 'zero-based' ? 'zero-based' : 'padded-visible');

      const rawPaddingRatio =
        highcharts_config?.yAxis?.padding_ratio ??
        highcharts_config?.yAxis_padding_ratio ??
        highcharts_config?.padding_ratio;
      const parsedPaddingRatio = Number(rawPaddingRatio);
      const normalizedPaddingRatio = Number.isFinite(parsedPaddingRatio)
        ? parsedPaddingRatio > 1 && parsedPaddingRatio <= 100
          ? parsedPaddingRatio / 100
          : parsedPaddingRatio
        : undefined;
      setYAxisPaddingRatio(
        typeof normalizedPaddingRatio === 'number' && normalizedPaddingRatio >= 0 && normalizedPaddingRatio < 1
          ? normalizedPaddingRatio
          : 0.2,
      );
      let shouldEnableSma = true;
      let nextSmaConfig: { defaultValue: number; values: number[] } | undefined;

      if (typeof sma === 'boolean') {
        shouldEnableSma = sma;
      } else if (typeof sma === 'string') {
        shouldEnableSma = sma.toLowerCase() !== 'false';
      } else if (sma && typeof sma === 'object' && !Array.isArray(sma)) {
        const rawValues = Array.isArray((sma as any).Values)
          ? (sma as any).Values
          : Array.isArray((sma as any).values)
            ? (sma as any).values
            : [];
        const parsedValues = (rawValues as unknown[])
          .map(value => Number(value))
          .filter(value => Number.isFinite(value));
        const rawDefaultValue = (sma as any).DefaultValue ?? (sma as any).defaultValue;
        const parsedDefaultValue = Number(rawDefaultValue);
        const hasDefault = Number.isFinite(parsedDefaultValue);

        if (parsedValues.length > 0) {
          const uniqueValues = Array.from(new Set(parsedValues));
          if (hasDefault && !uniqueValues.includes(parsedDefaultValue)) {
            uniqueValues.unshift(parsedDefaultValue);
          }
          const defaultValue = hasDefault ? parsedDefaultValue : uniqueValues[0];
          nextSmaConfig = { defaultValue, values: uniqueValues };
          shouldEnableSma = true;
        } else if (hasDefault) {
          nextSmaConfig = { defaultValue: parsedDefaultValue, values: [parsedDefaultValue] };
          shouldEnableSma = true;
        } else {
          shouldEnableSma = false;
        }
      }

      setSmaConfig(nextSmaConfig);
      setSmaEnabled(shouldEnableSma);
    } else {
      setUsedData({});
      setSmaEnabled(false);
      setSmaConfig(undefined);
      setMeanEnabled(false);
      setDropdownLegendEnabled(false);
      setDefaultTimeWindow(undefined);
      setYAxisRangeMode('padded-visible');
      setYAxisPaddingRatio(0.2);
    }
  }, [category, prism, data, filterValues, setUsedData, setColorScheme, setSmaEnabled, setSmaConfig]);

  useEffect(() => {
    setChartTitle(rawTitle || '');
  }, [rawTitle, category, prism]);

  return {
    data: usedData,
    colorScheme,
    chartTitle,
    chartSubtitle,
    chartYtitle,
    chartYtitle2,
    chartYmax,
    chartYmin,
    smaEnabled,
    smaConfig,
    meanEnabled,
    dropdownLegendEnabled,
    defaultTimeWindow,
    yAxisRangeMode,
    yAxisPaddingRatio,
    error,
    isLoading: isLoading,
  };
}

export function useScroller() {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const scrollToTop = useCallback(() => window.scrollTo({ top: 0, behavior: 'smooth' }), []);
  const scrollToBottom = useCallback(() => {
    scrollerRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const onScroll = () => setOffset(window.pageYOffset);
    window.removeEventListener('scroll', onScroll);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return { scrollerRef, scrollToTop, scrollToBottom, offset };
}
